import {
  callAccountGroups,
  findOneOrganization,
  Group,
  User,
  Uuid,
} from '@/api'
import { posthog } from '@/lib/posthog'
import type { EffortMetrics } from './declarationEffortTracker'

export type DeclarationAnalyticsContext = {
  dossier_id: string
  declaration_id?: string
  organization_id?: string | null
  agence_id?: string | null
}

export type DeclarationInterfaceVariant = 'classic' | 'sections'

export type DeclarationInterfaceEndReason =
  | 'switch'
  | 'declaration_change'
  | 'unmount'

export type DeclarationActionSummary = {
  has_field_edit: boolean
  has_review: boolean
  has_structure: boolean
  has_validate: boolean
  actions_count: number
  event_type_counts: Record<string, number>
}

const FIELD_EDIT_TYPES = new Set(['set', 'clear', 'clear_column', 'set_column'])
const REVIEW_TYPES = new Set(['review_issue'])
const STRUCTURE_TYPES = new Set([
  'add_line',
  'remove_line',
  'add_tuple',
  'remove_tuple',
  'consolidate',
  'unconsolidate',
])

/** Compte les écritures acceptées, sans leurs valeurs. Un lot vide ne compte aucune action. */
export const summarizeDeclarationEvents = (
  events: ReadonlyArray<{ type: string }>
): DeclarationActionSummary => {
  const event_type_counts: Record<string, number> = {}
  for (const event of events) {
    event_type_counts[event.type] = (event_type_counts[event.type] ?? 0) + 1
  }
  return {
    has_field_edit: events.some((event) => FIELD_EDIT_TYPES.has(event.type)),
    has_review: events.some((event) => REVIEW_TYPES.has(event.type)),
    has_structure: events.some((event) => STRUCTURE_TYPES.has(event.type)),
    has_validate: events.some((event) => event.type === 'validate'),
    actions_count: events.length,
    event_type_counts,
  }
}

export const declarationPassageBilan = (
  session: {
    variant: DeclarationInterfaceVariant
    startedAt: number
    actionsCount: number
    validated: boolean
  },
  now: number
): {
  interface_variant: DeclarationInterfaceVariant
  duration_sec: number
  actions_count: number
  validated: boolean
} => ({
  interface_variant: session.variant,
  duration_sec: Math.max(0, Math.round((now - session.startedAt) / 1000)),
  actions_count: session.actionsCount,
  validated: session.validated,
})

type PostHogGroups = {
  organisation?: string
  agence?: string
}

const buildGroups = (
  organizationId?: string | null,
  agenceId?: string | null
): PostHogGroups | undefined => {
  const groups: PostHogGroups = {}

  if (organizationId) {
    groups.organisation = organizationId
  }
  if (agenceId) {
    groups.agence = agenceId
  }

  return Object.keys(groups).length > 0 ? groups : undefined
}

export const buildDeclarationEventProperties = (
  context: DeclarationAnalyticsContext,
  extra: Record<string, unknown> = {}
): Record<string, unknown> => {
  const { dossier_id, declaration_id, organization_id, agence_id } = context
  const groups = buildGroups(organization_id, agence_id)

  return {
    dossier_id,
    ...(declaration_id ? { declaration_id } : {}),
    ...(organization_id ? { organization_id } : {}),
    ...(agence_id ? { agence_id } : {}),
    ...(groups ? { $groups: groups } : {}),
    ...extra,
  }
}

export const captureDeclarationEvent = (
  event: string,
  context: DeclarationAnalyticsContext,
  extra: Record<string, unknown> = {}
): void => {
  posthog.capture(event, buildDeclarationEventProperties(context, extra))
}

/** Annonces in-app (bandeaux de nouveauté) : vue, fermeture, clic sur l'action. */
export type AnnouncementAction =
  | 'viewed'
  | 'dismissed'
  | 'cta_clicked'
  | 'learn_more_clicked'

export const captureAnnouncementEvent = (
  action: AnnouncementAction,
  announcementKey: string
): void => {
  posthog.capture(`announcement_${action}`, { announcement_key: announcementKey })
}

/** Product tours (spec V1) : le taux de complétion par étape se lit dans PostHog. */
export type ProductTourAction =
  | 'started'
  | 'step_viewed'
  | 'completed'
  | 'dismissed'

export const captureProductTourEvent = (
  action: ProductTourAction,
  props: {
    tourKey: string
    version: number
    trigger: 'auto' | 'manual'
    stepIndex?: number
  }
): void => {
  posthog.capture(`product_tour_${action}`, {
    tour_key: props.tourKey,
    tour_version: props.version,
    trigger: props.trigger,
    ...(props.stepIndex !== undefined && { step_index: props.stepIndex }),
  })
}

export const captureDeclarationInterfaceViewed = (
  context: DeclarationAnalyticsContext,
  props: {
    interface_variant: DeclarationInterfaceVariant
    declaration_state?: string
    read_only: boolean
  }
): void => {
  captureDeclarationEvent('declaration_interface_viewed', context, {
    interface_variant: props.interface_variant,
    ...(props.declaration_state
      ? { declaration_state: props.declaration_state }
      : {}),
    read_only: props.read_only,
    $set: { declaration_interface_variant: props.interface_variant },
  })
}

export const captureDeclarationInterfaceSwitched = (
  context: DeclarationAnalyticsContext,
  props: {
    from: DeclarationInterfaceVariant
    to: DeclarationInterfaceVariant
    duration_sec: number
    actions_count: number
    validated: boolean
  }
): void => {
  captureDeclarationEvent('declaration_interface_switched', context, {
    from: props.from,
    to: props.to,
    interface_variant: props.from,
    duration_sec: props.duration_sec,
    actions_count: props.actions_count,
    validated: props.validated,
    $set: { declaration_interface_variant: props.to },
  })
}

export const captureDeclarationActionApplied = (
  context: DeclarationAnalyticsContext,
  props: DeclarationActionSummary & {
    interface_variant: DeclarationInterfaceVariant
  }
): void => {
  captureDeclarationEvent('declaration_action_applied', context, props)
}

export const captureDeclarationInterfaceSessionEnded = (
  context: DeclarationAnalyticsContext,
  props: {
    interface_variant: DeclarationInterfaceVariant
    duration_sec: number
    actions_count: number
    validated: boolean
    end_reason: DeclarationInterfaceEndReason
  }
): void => {
  captureDeclarationEvent('declaration_interface_session_ended', context, props)
}

export const captureDeclarationCompleted = (
  context: DeclarationAnalyticsContext,
  metrics: EffortMetrics
): void => {
  captureDeclarationEvent('declaration_completed_client', context, {
    clicks_count: metrics.clicks_count,
    duration_sec: metrics.duration_sec,
    manual_corrections: metrics.manual_corrections,
  })
}

export const registerGlobalAnalyticsProperties = (properties: {
  organization_id?: string
}): void => {
  if (!properties.organization_id) return
  posthog.register(properties)
}

export const setupPostHogGroups = async (
  user: Partial<User>
): Promise<void> => {
  const organizationId = user.organization
  if (!organizationId) return

  try {
    const organization = await findOneOrganization(organizationId as Uuid)
    posthog.group('organisation', organizationId, {
      name: organization.name,
      signup_date: organization.createdAt,
    })
  } catch (error) {
    console.error('Failed to set PostHog organisation group', error)
    posthog.group('organisation', organizationId, {})
  }

  const userGroupIds = user.groups ?? []
  if (userGroupIds.length === 0) return

  try {
    const response = await callAccountGroups()
    const allGroups = (response.data ?? []) as Group[]
    const userAgencies = allGroups.filter(
      (group) => group.gid && userGroupIds.includes(group.gid)
    )

    for (const agency of userAgencies) {
      if (!agency.gid) continue
      posthog.group('agence', agency.gid, {
        name: agency.name,
        organization_id: agency.organization,
      })
    }
  } catch (error) {
    console.error('Failed to set PostHog agence groups', error)
  }
}

export const identifyPostHogUser = (user: Partial<User>): void => {
  if (!user.uid || !user.email) return

  posthog.identify(user.uid, {
    email: user.email,
    name: user.profile?.firstName,
  })
}
